import { accountActions } from '@cmctechnology/webinvest-store-accounts';
import {
  HOME_PAGE_TYPES,
  OPEN_SELF_URL_TARGET,
  frontendActions,
  getConfig,
  useDefaultHomePage,
  useFrontendContext
} from '@cmctechnology/webinvest-store-frontend';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes as RouterRoutes, useLocation, useNavigate, useParams } from 'react-router';
import { Authenticated } from './components/Authenticated';
import {
  AccountDetailsPage,
  BanyanReports,
  Dashboard,
  FGLoginManager,
  ModuleConfigs,
  NavCanvas,
  PassThroughIFrame,
  PricingSubscription,
  SfOmniForm,
  WelcomeTour
} from './Modules';
import { PostMessageManager } from './hocs/PostMessageManager';
import { IStore } from './store/store';
import { ThemeOverride } from './components/ThemeOverride';
import { themeConfig, Theme, THEMES, getThemeConfig, THEME_PATHS } from '@cmctechnology/webinvest-constants';
import { Logout } from './pages/Logout';
import { RedirectToMobileApp } from './pages/RedirectToMobileApp';
import { usePreloadModule } from '@cmctechnology/webinvest-module';
import { MainOutlet } from './components/MainOutlet';
import { MaintenanceBanner } from './components/MaintenanceBanner';
import { LOCALSTORAGE_THEME, SPLASH_SCREEN_PROPS } from './constants/constants';
import { GenericWidgetContainer } from './components/GenericWidgetContainer';
import { themes } from '@cmctechnology/webinvest-design-system';
import { t } from 'i18next';
import { Platforms } from './pages/Platforms';
import { ProSubscription } from './pages/ProSubscription';
import { ProUnsubscribe } from './pages/ProUnsubscribe';
import { SalesforceOmniFormPage } from './pages/SalesforceOmniFormPage';
import { SplashScreenZoomDisabledWrapper } from './components/SplashScreenZoomDisabledWrapper';

const routePrefix = `/trading`;
const getUrl = () => getConfig().stockbrokingUrl;
/* istanbul ignore next */
const sitePrefix = process.env.PUBLIC_URL ?? '';
// eslint-disable-next-line i18next/no-literal-string
export const TRADING_IFRAME_ID = `root_trading_frame`;
const sfModuleUrl = () => `${getConfig().hostUrl}/webinvest-module-salesforce-omni-page/v/latest`;

const PassThrough: React.FC = () => (
  <PassThroughIFrame
    loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
    frameId={TRADING_IFRAME_ID}
    url={getUrl()}
    sitePrefix={sitePrefix}
    routePrefix={routePrefix}
  />
);

// Routes that don't need the nav
const noNavPages = [
  `Manage/QuickTicket`,
  `Trade/QuickTicket`,
  `Trade/BulkOrders`,
  `mFund/QuickTicket`,
  `IntermediaryCompany/OnlineApplication`,
  `Pro/Unsubscribe`,
  `StockCodeLookup/search.aspx`,
  `help/copy/HelpPage_Confirmations.aspx`,
  `help/copy/HelpPage_IRS.aspx`,
  `help/copy/HelpPage_MarketDepth.aspx`,
  `help/copy/HelpPage_Options.aspx`,
  `help/copy/HelpPage_OrderHistory.aspx`,
  `help/copy/HelpPage_ProfitLoss.aspx`,
  `help/copy/HelpPage_TicketStyle.aspx`,
  `help/copy/HelpPage_Warrants.aspx`,
  `MarketInformation/TheScreenerHelper`,
  `AnalyseStocks/Floats/FloatInfo.aspx`,
  `help/conditional-faq.aspx`,
  `net/UI/Chart/AdvancedChart.aspx`,
  `StockDetails/ChartHelp/Help.aspx`,
  `UserSettings/MarketDataTerms.aspx`,
  `help/brokerage.aspx`,
  `MFund/Faq`,
  `net/ui/research/Glossary.aspx`,
  `Aeoi/LodgeForm`,
  `Manage/WatchlistBreakout`,
  `Manage/WatchlistBreakout/:id`,
  `ShareSight/ConnectSharesight.aspx`,
  `Support/PrintApplication`
];

const redirectToTrading = [`sharesight/connectsharesight.aspx/register/:id`];

const RedirectToTrading = () => {
  const { config } = useFrontendContext();
  const { '*': splat } = useParams();
  useEffect(() => {
    const path = `${config.stockbrokingUrl}/${splat}`;
    frontendActions.redirectToUrl(path, OPEN_SELF_URL_TARGET);
  }, []);
  return <></>;
};

export const RedirectToDefaultPage = () => {
  const { tradingRoutePrefix } = useFrontendContext();
  const [homePageType, , selectedMarket] = useDefaultHomePage();

  switch (homePageType) {
    case HOME_PAGE_TYPES.MARKET: {
      return <Navigate to={`${tradingRoutePrefix}/clients/homepage.aspx?country=${selectedMarket}`} />;
    }
    case HOME_PAGE_TYPES.PROFIT_AND_LOSS: {
      return <Navigate to={`${tradingRoutePrefix}/Manage/ProfitLoss`} />;
    }
    default: {
      return <Navigate to={`${process.env.PUBLIC_URL}/dashboard`} />;
    }
  }
};

const noTrackingThemePaths = new Set([THEME_PATHS.asb, THEME_PATHS.stgeorge, THEME_PATHS.bendigo, THEME_PATHS.westpac]);

const usePreLoadModules = () => {
  usePreloadModule(ModuleConfigs.SalesforceChatAgent);
  usePreloadModule(ModuleConfigs.ProfitLoss);
  usePreloadModule(ModuleConfigs.ProfitLossAccBalance);
  usePreloadModule(ModuleConfigs.Watchlists);
  usePreloadModule(ModuleConfigs.PassThroughIFrame);
  usePreloadModule(ModuleConfigs.NavCanvas);
};

export const Routes: React.FC = () => {
  const dispatch = useDispatch();
  const crc = useSelector((store: IStore) => store.client.crc);
  const { tradingRoutePrefix, publicUrl } = useFrontendContext();

  const memoFGLoginManager = useMemo(() => <FGLoginManager loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />} />, []);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  usePreLoadModules();

  const navigate = useNavigate();
  (window as any).pushpath = (path: string) => {
    navigate(path);
  };

  useEffect(() => {
    if (!crc) return;

    // dispatch action to get one-off data thats required after login
    dispatch<any>(accountActions.fetchLeanAccounts());
  }, [crc]);

  const routes: { path: string; theme: Theme }[] = Object.entries(themeConfig).map(([theme, config]) => ({
    path: `/${config.themePath}/*`,
    theme: theme as Theme
  }));
  const latestTheme = (localStorage.getItem(LOCALSTORAGE_THEME) as Theme) || THEMES.CMC;
  const currentThemeConfig = getThemeConfig(latestTheme);
  const themePath = currentThemeConfig.themePath;
  const accessFalconToggle = useSelector((store: IStore) => store.client.settings.AccessFalconToggle);
  const eoraTheme = themes[latestTheme];
  return (
    <>
      <link
        id='favicon'
        rel='icon'
        type='image/png'
        href={`${process.env.REACT_APP_BASE_ASSET_URL}${eoraTheme?.logos.mark.color ?? '/favicon.ico'}`}
        sizes='16x16'
        data-rh='true'
      />
      {/* istanbul ignore next */ noTrackingThemePaths.has(themePath as any) && <meta name='robots' content='noindex, nofollow' />}
      <PostMessageManager />
      <RouterRoutes>
        <Route
          path={`${process.env.PUBLIC_URL}/*`}
          element={
            <MainOutlet>
              <RouterRoutes>
                <Route path='/oidc' element={<Navigate to={`${process.env.PUBLIC_URL}/${themePath}/login/oidc${window.location.search}`} />} />
                <Route path='/redirectToApp' element={<RedirectToMobileApp />} />
                <Route path='/login/*' element={<Navigate to={`${process.env.PUBLIC_URL}/${themePath}/login`} />} />
                <Route path={`/default`} element={<RedirectToDefaultPage />} />
                <Route path='/dashboard/*' element={<Authenticated />}>
                  <Route
                    element={
                      <NavCanvas
                        loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
                        tradingRoutePrefix={tradingRoutePrefix}
                        publicUrl={publicUrl}
                      />
                    }
                  >
                    <Route
                      path='*'
                      element={
                        <>
                          {accessFalconToggle && <WelcomeTour loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />} />}
                          <title>{`${t('Market Information Services')} - ${currentThemeConfig.docTitle}`}</title>
                          <Dashboard loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />} tradingRoutePrefix={tradingRoutePrefix} />
                        </>
                      }
                    />
                  </Route>
                </Route>
                {(latestTheme === THEMES.CMCAlpha || latestTheme === THEMES.Westpac) && (
                  <Route path='/banyantreereports/*' element={<Authenticated />}>
                    <Route
                      element={<NavCanvas loadingElement={<SplashScreenZoomDisabledWrapper />} tradingRoutePrefix={tradingRoutePrefix} publicUrl={publicUrl} />}
                    >
                      <Route
                        path='*'
                        element={
                          <>
                            <title>{`${t('Banyan Tree Research')} - ${currentThemeConfig.docTitle}`} </title>
                            <BanyanReports loadingElement={<SplashScreenZoomDisabledWrapper />} />
                          </>
                        }
                      />
                    </Route>
                  </Route>
                )}
                {routes.map((route) => (
                  <Route
                    path={route.path}
                    key={route.theme}
                    element={
                      <ThemeOverride themeOverride={route.theme}>
                        <MaintenanceBanner>{memoFGLoginManager}</MaintenanceBanner>
                      </ThemeOverride>
                    }
                  />
                ))}
                <Route path='/trading/apply/*' element={<PassThrough />} />
                <Route path='/trading/*' element={<Authenticated />}>
                  {redirectToTrading.map((route) => (
                    <Route key={route} path={route} element={<RedirectToTrading />} />
                  ))}
                  {noNavPages.map((route) => (
                    <Route key={route} path={route} element={<PassThrough />} />
                  ))}
                  <Route
                    element={
                      searchParams.get('popup') === `true` || searchParams.get(`iframe`) ? undefined : (
                        <NavCanvas
                          loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
                          tradingRoutePrefix={tradingRoutePrefix}
                          publicUrl={publicUrl}
                        />
                      )
                    }
                  >
                    <Route path='*' element={<PassThrough />} />
                  </Route>
                </Route>
                <Route path='/logout/*' element={<Logout />} />
                <Route path='/pricingSubscription/*' element={<Authenticated />}>
                  <Route
                    element={
                      <NavCanvas
                        loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
                        tradingRoutePrefix={tradingRoutePrefix}
                        publicUrl={publicUrl}
                      />
                    }
                  >
                    <Route path='*' element={<PricingSubscription tradingRoutePrefix={tradingRoutePrefix} />} />
                  </Route>
                </Route>
                <Route path='/stockHeatMap/*' element={<Authenticated />}>
                  <Route
                    element={
                      <NavCanvas
                        loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
                        tradingRoutePrefix={tradingRoutePrefix}
                        publicUrl={publicUrl}
                      />
                    }
                  >
                    <Route path='*' element={<GenericWidgetContainer genericWidgetType='stockHeatmap' />} />
                  </Route>
                </Route>
                <Route path='/etfHeatMap/*' element={<Authenticated />}>
                  <Route
                    element={
                      <NavCanvas
                        loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
                        tradingRoutePrefix={tradingRoutePrefix}
                        publicUrl={publicUrl}
                      />
                    }
                  >
                    <Route path='*' element={<GenericWidgetContainer genericWidgetType='etfHeatmap' />} />
                  </Route>
                </Route>
                <Route path='/TipRanksNews/*' element={<Authenticated />}>
                  <Route
                    element={
                      <NavCanvas
                        loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
                        tradingRoutePrefix={tradingRoutePrefix}
                        publicUrl={publicUrl}
                      />
                    }
                  >
                    <Route path='*' element={<GenericWidgetContainer genericWidgetType='TipRanksNews' />} />
                  </Route>
                </Route>
                <Route path='/screener/*' element={<Authenticated />}>
                  <Route
                    element={
                      <NavCanvas
                        loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
                        tradingRoutePrefix={tradingRoutePrefix}
                        publicUrl={publicUrl}
                      />
                    }
                  >
                    <Route path='*' element={<GenericWidgetContainer genericWidgetType='TradingViewScreener' />} />
                  </Route>
                </Route>
                <Route path='/platforms/*' element={<Authenticated />}>
                  <Route
                    element={
                      <NavCanvas
                        loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
                        tradingRoutePrefix={tradingRoutePrefix}
                        publicUrl={publicUrl}
                      />
                    }
                  >
                    <Route path='pro/subscription' element={<ProSubscription />} />
                    <Route path='pro/unsubscribe' element={<ProUnsubscribe />} />
                    <Route path='*' element={<Platforms />} />
                  </Route>
                </Route>
                <Route path='/accountDetails/*' element={<Authenticated />}>
                  <Route
                    element={
                      <NavCanvas
                        loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
                        tradingRoutePrefix={tradingRoutePrefix}
                        publicUrl={publicUrl}
                      />
                    }
                  >
                    <Route path='*' element={<AccountDetailsPage />} />
                  </Route>
                </Route>
                <Route path='/omniForm/*' element={<Authenticated />}>
                  <Route
                    element={
                      <NavCanvas
                        loadingElement={<SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
                        tradingRoutePrefix={tradingRoutePrefix}
                        publicUrl={publicUrl}
                      />
                    }
                  >
                    <Route path='*' element={<SalesforceOmniFormPage pageName={searchParams.get('form') ?? undefined} hostUrl={sfModuleUrl()} />} />
                  </Route>
                </Route>
                <Route path='/mobileForm/*' element={<SfOmniForm pageName={searchParams.get('form') ?? undefined} hostUrl={sfModuleUrl()} />} />

                <Route path='/*' element={<Navigate to={`${process.env.PUBLIC_URL}/default`} />} />
              </RouterRoutes>
            </MainOutlet>
          }
        />
      </RouterRoutes>
    </>
  );
};
